import type { ProductListItem } from '@travel-planner/shared/schemas';
import type { DivIcon, PathOptions } from 'leaflet';
import type { MapBounds, MapPoint } from '@/shared/map';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Polygon, Tooltip } from 'react-leaflet';
import { getMarkerGlyph } from '@/shared/lib/category-icon';
import {
  buildMapIcon,
  CountryFocus,
  FitBounds,
  FlyTo,
  LeafletMap,
  MapPopup,
  MapSearchControl,
  useHoveredMarkerStore,
} from '@/shared/map';
import { Select } from '@/shared/ui/select';
import { Spinner } from '@/shared/ui/spinner';
import { Text } from '@/shared/ui/text';
import { buildProductMarkers, selectFitPoints } from '../model/product-markers';
import css from './products-map.module.scss';

type RegionTone = 'mood' | 'listed' | 'active';
type RegionPalette = 'blue' | 'soft';

// `soft` (Mini Planner): warm grey with thin, faint borders, so the regions
// sit behind the markers instead of competing with them.
const REGION_PATHS: Record<RegionPalette, Record<RegionTone, PathOptions>> = {
  blue: {
    active: { color: '#2563eb', fillOpacity: 0.22, weight: 3 },
    listed: { color: '#2563eb', fillOpacity: 0.08, weight: 2 },
    mood: { color: '#c4a574', fillOpacity: 0.12, weight: 1 },
  },
  soft: {
    active: {
      color: '#5e5b54',
      opacity: 0.45,
      weight: 1.5,
      fillColor: '#8a877f',
      fillOpacity: 0.16,
    },
    listed: {
      color: '#8a877f',
      opacity: 0.35,
      weight: 1,
      fillColor: '#a8a49b',
      fillOpacity: 0.08,
    },
    mood: { color: '#c4a574', opacity: 0.45, weight: 1, fillOpacity: 0.08 },
  },
};

// Returns a cached category/subcategory divIcon, building it on first
// encounter so the static SVG markup is rendered once per glyph, not per
// marker/render.
const getOrBuildIcon = (
  cache: Map<string, DivIcon>,
  category: string | null,
  subcategory: string | null,
  highlighted = false
): DivIcon => {
  const key = `${category ?? '__none__'}|${subcategory ?? '__none__'}|${highlighted ? 'hl' : ''}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const { Icon, strokeWidth } = getMarkerGlyph(category, subcategory);
  const icon = buildMapIcon({ Icon, strokeWidth, highlighted });
  cache.set(key, icon);
  return icon;
};

type Props = {
  products: ReadonlyArray<ProductListItem>;
  isLoading?: boolean;
  isError?: boolean;
  // Fires on a USER pan/zoom settle (and place-search recentre) with the
  // viewport bbox — programmatic fits are suppressed. Providing this puts
  // the map in "bounds mode": the host writes the bbox to its query so the
  // table + map narrow to what's in view. Omit it for mirror-only hosts.
  onMoveEnd?: (bounds: MapBounds) => void;
  // Marker click → host decides (open a detail modal, route to /products/$id).
  onProductClick?: (id: string) => void;
  // `tooltip` (default): hovering a marker opens its tooltip, and a row
  // hovered in a list pins it. `sync`: no tooltips at all — the hovered
  // marker (on the map, or through its row in the list) grows, turns grey
  // and pulses, and a marker hover is reported to the list
  // (`useHoveredMarkerStore`, source `map`), which highlights the row.
  markerHover?: 'tooltip' | 'sync';
  // Mirror-mode auto-fit: continuously fit the viewport to the marker set.
  // Ignored in bounds mode (where the products fit is one-shot so it doesn't
  // fight the user's pans). Set false to never auto-fit to products.
  fitToProducts?: boolean;
  // Custom-region picker (M5). Rendered only when `regionOptions` is given.
  // Selecting a region narrows the host's product query (host wires it) and
  // draws `regionBoundary` as a polygon, fitting the view to it.
  regionOptions?: ReadonlyArray<{ label: string; value: string }>;
  selectedRegionId?: string | null;
  onRegionChange?: (id: string | null) => void;
  regionBoundary?: ReadonlyArray<MapPoint> | null;
  regionBoundaries?: ReadonlyArray<ReadonlyArray<MapPoint>>;
  // Optional styled overlays (advisor: mood / listed / active). When set,
  // these replace `regionBoundaries` for draw + fit. Fit prefers `active`.
  regionLayers?: ReadonlyArray<{
    points: ReadonlyArray<MapPoint>;
    tone?: RegionTone;
  }>;
  // Colours for the region outlines: `blue` (default) or `soft` grey.
  regionPalette?: RegionPalette;
  // Selected country name to centre the map on (legacy parity). The host
  // passes the last-selected country; when it changes the map geocodes it and
  // recentres there. `null`/undefined leaves the view as-is.
  focusCountry?: string | null;
  // Bumped by the host's "Reset all filters" to clear the place-search input.
  // The search text is local, uncontrolled state in `MapSearchControl`, so we
  // remount it (via `key`) when this token changes.
  searchResetToken?: number;
  // Language to read marker titles in. The planner passes the proposal's
  // language so tooltips match the picker cards beside them; omitting it
  // keeps the English-first default (/products).
  languageCode?: string | null;
};

// Layer 2a: the products/source map. Presentational + intent-emitting —
// the host owns the query + URL. Built on the `@/shared/map` foundation;
// reused by the planner pane and (later) the /products page.
export const ProductsMap = ({
  products,
  isLoading = false,
  isError = false,
  onMoveEnd,
  onProductClick,
  markerHover = 'tooltip',
  fitToProducts = true,
  regionOptions,
  selectedRegionId,
  onRegionChange,
  regionBoundary,
  regionBoundaries,
  regionLayers,
  regionPalette = 'blue',
  focusCountry,
  searchResetToken,
  languageCode,
}: Props) => {
  // List→map hover sync: when a product row (picker / table) is hovered,
  // the matching marker shows its popup. Setting the matching tooltip
  // `permanent` (keyed so react-leaflet re-binds it) opens it without
  // needing the cursor on the marker.
  const hoveredId = useHoveredMarkerStore((s) => s.hoveredId);
  const setHovered = useHoveredMarkerStore((s) => s.setHovered);
  const syncHover = markerHover === 'sync';

  // Place-search recenter (M4): selecting a geocode result flies the map
  // there. Pure view move — it doesn't filter the product list. The search
  // input owns its own text + clear-× (legacy parity); the region filter is
  // cleared via the dropdown's "All regions" option.
  const [flyTarget, setFlyTarget] = useState<MapPoint | null>(null);

  // "Reset all filters" must also reset the map *view* (not just the search
  // text): drop any place-search recenter so a stale flyTarget can't re-apply,
  // and let the products fit re-run below (its key includes the reset token).
  useEffect(() => {
    setFlyTarget(null);
  }, [searchResetToken]);

  const markers = useMemo(
    () => buildProductMarkers(products, languageCode),
    [products, languageCode]
  );

  // When the host drives the query from the viewport (`onMoveEnd` provided),
  // the map is in "bounds mode": it fits to products only ONCE on first load
  // (then the user's pans/zooms own the viewport), and that initial fit is
  // programmatic so it doesn't refetch.
  const boundsMode = !!onMoveEnd;

  const layers = useMemo(() => {
    if (regionLayers && regionLayers.length > 0) {
      return regionLayers.filter((layer) => layer.points.length >= 3);
    }
    const rings =
      regionBoundaries && regionBoundaries.length > 0
        ? regionBoundaries.filter((ring) => ring.length >= 3)
        : regionBoundary && regionBoundary.length >= 3
          ? [regionBoundary]
          : [];
    return rings.map((points) => ({
      points,
      tone: 'listed' as const,
    }));
  }, [regionLayers, regionBoundaries, regionBoundary]);

  const combinedBoundary = useMemo(() => {
    const active = layers.find((layer) => layer.tone === 'active');
    if (active) return [...active.points];
    // Styled layers without an active chapter: draw them, but let the host's
    // CountryFocus own the camera (CMS country-wide browse).
    if (regionLayers && regionLayers.length > 0) return [];
    return layers.flatMap((layer) => [...layer.points]);
  }, [layers, regionLayers]);

  // Fit priority: selected region boundary(ies) win (recenter to the
  // region), else the product markers. In bounds mode the products fit is
  // one-shot; in mirror mode it tracks the markers continuously.
  const { regionPoints, productPoints } = useMemo(
    () =>
      selectFitPoints({
        regionBoundary: combinedBoundary,
        markers,
        boundsMode,
        fitToProducts,
      }),
    [combinedBoundary, markers, boundsMode, fitToProducts]
  );

  const iconCache = useRef(new Map<string, DivIcon>());

  return (
    <div className={css.root}>
      {isLoading && (
        <div className={css.overlay} data-testid="products-map-loading">
          <Spinner />
        </div>
      )}
      {/* Error renders as a banner over the map rather than unmounting it,
          so a transient (e.g. network) error doesn't reset the user's
          pan/zoom on recovery. */}
      {isError && (
        <div className={css.errorBanner} role="alert">
          <Text variant="error">Failed to load products.</Text>
        </div>
      )}
      <div className={css.controls}>
        <MapSearchControl
          key={searchResetToken}
          onSelect={(result) =>
            setFlyTarget({ lat: result.latitude, lng: result.longitude })
          }
          placeholder="Search country or city..."
        />
        {regionOptions && regionOptions.length > 0 && (
          <Select
            onChange={(e) => onRegionChange?.(e.target.value || null)}
            options={[
              { label: 'All regions', value: '' },
              ...regionOptions.map((r) => ({ label: r.label, value: r.value })),
            ]}
            size="default"
            value={selectedRegionId ?? ''}
          />
        )}
      </div>
      <LeafletMap className={css.map} onMoveEnd={onMoveEnd}>
        {/* Distinct keys so switching region↔products remounts a fresh
            FitBounds — without them React reuses one instance and its
            one-shot `hasFitRef` stays set, so clearing the region wouldn't
            re-fit to the products.

            While a country is focused, `CountryFocus` owns the view, so the
            products fit is suppressed (it would fight the country recentre).
            Clearing the country REMOUNTS this FitBounds, which re-fits to the
            (now unfiltered) products — i.e. reverts to the initial all-
            products view (legacy parity: clearing the country resets the map
            to its default position). */}
        {regionPoints.length > 0 ? (
          <FitBounds key="region-fit" points={regionPoints} />
        ) : (
          !focusCountry &&
          productPoints.length > 0 && (
            // Reset remounts this (key includes the reset token), re-running
            // the one-shot products fit so the map zooms back to all products —
            // its default view before any pan/zoom/place-search.
            <FitBounds
              key={`products-fit-${searchResetToken ?? 0}`}
              once={boundsMode}
              points={productPoints}
            />
          )
        )}
        <FlyTo target={flyTarget} zoom={11} />
        <CountryFocus name={focusCountry} />
        {layers.map((layer, index) => {
          const tone = layer.tone ?? 'listed';
          return (
            <Polygon
              key={`region-poly-${tone}-${index}`}
              pathOptions={REGION_PATHS[regionPalette][tone]}
              positions={layer.points.map((p) => [p.lat, p.lng])}
            />
          );
        })}
        {markers.map((marker) => (
          <Marker
            eventHandlers={{
              ...(onProductClick
                ? { click: () => onProductClick(marker.id) }
                : {}),
              ...(syncHover
                ? {
                    mouseover: () => setHovered(marker.id, 'map'),
                    mouseout: () => setHovered(null, 'map'),
                  }
                : {}),
            }}
            icon={getOrBuildIcon(
              iconCache.current,
              marker.category,
              marker.subcategory,
              syncHover && hoveredId === marker.id
            )}
            key={marker.id}
            position={[marker.point.lat, marker.point.lng]}
            zIndexOffset={syncHover && hoveredId === marker.id ? 1000 : 0}
          >
            {syncHover ? null : (
              <Tooltip
                className="products-map-tip"
                key={hoveredId === marker.id ? 'pinned' : 'hover'}
                permanent={hoveredId === marker.id}
              >
                <MapPopup thumbnail={marker.thumbnail} title={marker.title} />
              </Tooltip>
            )}
          </Marker>
        ))}
      </LeafletMap>
      {!(isLoading || isError) && markers.length === 0 && (
        <div className={css.hint}>
          <Text size="sm" variant="muted">
            No products with a location to show on the map.
          </Text>
        </div>
      )}
    </div>
  );
};
